import { IconAlertCircle } from '@developerz/ui';
import { useLocation } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { IdentityLinkPanel } from '../components/IdentityLinkPanel';
import { IdentityTable } from '../components/IdentityTable';
import { LinkCandidatesPanel } from '../components/LinkCandidatesPanel';
import { LinkOutcomeNotice } from '../components/LinkOutcomeNotice';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { t } from '../i18n';
import { createConfirmGate } from '../lib/confirm-gate';
import {
  type Identity,
  linkOutcome,
  removeIdentity,
  removeRefusalKey,
  useIdentities,
} from '../lib/identities';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN, useSessionMe } from '../lib/session';

// Container for Connected logins (#466): the person's logins
// (`GET /v1/me/identities`), the remove (`DELETE …/:id`, confirmed first), and
// one link form per org whose own IdP they could link (`/v1/me` memberships, as
// the SESSION — the list is the person's, whatever org the URL names). The link
// round-trip comes back to this page with `?linked=` / `?link_error=`, which
// `LinkOutcomeNotice` renders. `token` is injectable for tests (mirrors
// GithubToken); the router mounts it with no props → the session.
//
// Under the links, the sign-ins that share this person's verified email
// (#7426, `<LinkCandidatesPanel/>`): mounted once `/v1/me` names a USER, since
// the route 403s a credential with no human behind it.

export function ConnectedLogins(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const query = useIdentities(token);
  const me = useSessionMe(token);
  const location = useLocation();
  const [removeError, setRemoveError] = createSignal<string | null>(null);

  // A refusal is shown inline, never swallowed: the refusals the page can explain
  // get their own sentence (`removeRefusalKey`), anything else the API's `detail`.
  const remove = createMutation({
    call: (row: Identity) => removeIdentity(row.id, token),
    fallback: t('logins.remove_error'),
    onError: (message, cause) => {
      const key = removeRefusalKey(cause);
      setRemoveError(key === null ? message : t(key));
    },
    onSettled: () => query.refetch(),
  });
  const gate = createConfirmGate<Identity>({ act: (row) => remove.run(row) });

  return (
    <>
      <PageHeader title={t('logins.title')} desc={t('logins.subtitle')} />
      <LinkOutcomeNotice outcome={linkOutcome(location.search)} />
      <Show when={token !== null} fallback={<EmptyState message={t('logins.no_session')} />}>
        <Show when={removeError()}>
          {(message) => (
            <div class="alert alert--icon alert--destructive" role="alert">
              <IconAlertCircle />
              <p class="alert__desc">{message()}</p>
            </div>
          )}
        </Show>
        <QueryBoundary
          query={query}
          loading={<CardGridSkeleton listClass="identity-links__loading" count={1} />}
          isEmpty={(data) => data.data.length === 0}
          empty={<EmptyState message={t('logins.empty')} />}
        >
          {(data) => (
            <Panel title={t('logins.table_label')}>
              <IdentityTable
                rows={data.data}
                busyId={remove.pending() ? (gate.pending()?.id ?? null) : null}
                onRemove={(row) => {
                  setRemoveError(null);
                  gate.ask(row);
                }}
              />
            </Panel>
          )}
        </QueryBoundary>
        <IdentityLinkPanel memberships={me.data?.memberships ?? []} />
        <Show when={me.data?.user}>
          <LinkCandidatesPanel token={token} />
        </Show>
      </Show>
      <ConfirmDialog
        open={gate.pending() !== null}
        title={t('logins.remove_title')}
        desc={t('logins.remove_desc')}
        cancelLabel={t('logins.cancel')}
        actionLabel={t('logins.remove_confirm')}
        destructive
        onCancel={gate.cancel}
        onConfirm={() => void gate.confirm()}
      />
    </>
  );
}
